import { lazy, Suspense } from "react";
import { Navigate, Outlet, useRoutes } from "react-router-dom";
import AdminRoute from "../../components/AdminRoute";
import RedirectIfAuthenticated from "../../components/RedirectIfAuthenticated";
import PageLoader from "../../components/PageLoader";

// Auth pages
const AdminLoginPage = lazy(
  () => import("../../features/admin/pages/AdminLoginPage"),
);
const ForgotPasswordPage = lazy(
  () => import("../../features/auth/pages/ForgotPassword"),
);

// Admin Layout
const AdminLayout = lazy(
  () => import("../../features/admin/components/AdminLayout"),
);

// Admin pages
const AdminDashboard = lazy(
  () => import("../../features/admin/pages/AdminDashboard"),
);

// Bookings
const BookingsPage = lazy(
  () => import("../../features/admin/pages/BookingsPage"),
);
const BookingDetailPage = lazy(
  () => import("../../features/admin/pages/BookingDetailPage"),
);

// Users
const UsersPage = lazy(() => import("../../features/admin/pages/UsersPage"));
const UserDetailPage = lazy(
  () => import("../../features/admin/pages/UserDetailPage"),
);

// Providers
const ProvidersPage = lazy(
  () => import("../../features/admin/pages/ProvidersPage"),
);
const ProviderDetailPage = lazy(
  () => import("../../features/admin/pages/ProviderDetailPage"),
);

// Approvals
const ApprovalsPage = lazy(
  () => import("../../features/admin/pages/ApprovalsPage"),
);

// Categories
const CategoriesPage = lazy(
  () => import("../../features/admin/pages/CategoriesPage"),
);

// Pricing Configs
const PricingConfigsPage = lazy(
  () => import("../../features/admin/pages/PricingConfigsPage"),
);

// Payments
const PaymentsPage = lazy(
  () => import("../../features/admin/pages/PaymentsPage"),
);

// Reviews
const ReviewsPage = lazy(
  () => import("../../features/admin/pages/ReviewsPage"),
);

// Settings
const SettingsPage = lazy(
  () => import("../../features/admin/pages/SettingsPage"),
);

// Error
const NotFoundPage = lazy(() => import("../../features/error/NotFoundPage"));

const AdminApp = () => {
  const appRoutes = [
    // Auth routes (no layout)
    {
      path: "auth/login",
      element: (
        <RedirectIfAuthenticated redirectTo="/dashboard">
          <AdminLoginPage />
        </RedirectIfAuthenticated>
      ),
    },
    {
      path: "auth/forgot-password",
      element: (
        <RedirectIfAuthenticated redirectTo="/dashboard">
          <ForgotPasswordPage />
        </RedirectIfAuthenticated>
      ),
    },

    // Protected admin routes (with layout)
    {
      element: (
        <AdminRoute>
          <AdminLayout>
            <Outlet />
          </AdminLayout>
        </AdminRoute>
      ),
      children: [
        // Dashboard
        {
          path: "/",
          element: <Navigate to="/dashboard" replace />,
        },
        {
          path: "dashboard",
          element: <AdminDashboard />,
        },

        // Bookings
        {
          path: "bookings",
          element: <BookingsPage />,
        },
        {
          path: "bookings/:bookingId",
          element: <BookingDetailPage />,
        },

        // Users
        {
          path: "users",
          element: <UsersPage />,
        },
        {
          path: "users/:userId",
          element: <UserDetailPage />,
        },

        // Providers
        {
          path: "providers",
          element: <ProvidersPage />,
        },
        {
          path: "providers/:providerId",
          element: <ProviderDetailPage />,
        },

        // Approvals
        {
          path: "approvals",
          element: <ApprovalsPage />,
        },

        // Categories
        {
          path: "categories",
          element: <CategoriesPage />,
        },

        // Pricing Configs
        {
          path: "pricing-configs",
          element: <PricingConfigsPage />,
        },

        // Payments
        {
          path: "payments",
          element: <PaymentsPage />,
        },

        // Reviews
        {
          path: "reviews",
          element: <ReviewsPage />,
        },

        // Settings
        {
          path: "settings",
          element: <SettingsPage />,
        },
      ],
    },

    // 404
    {
      path: "*",
      element: <NotFoundPage />,
    },
  ];

  return useRoutes(appRoutes);
};

export default function AdminAppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <AdminApp />
    </Suspense>
  );
}
